import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { HiCheck } from "react-icons/hi";
import {
  FiPlus,
  FiChevronUp,
  FiChevronDown,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiUploadCloud,
} from "react-icons/fi";
import {
  useFetchAllMenuItemsMutation,
  useAddAppUserMenuMutation,
  useUpdateAppUserMenuMutation,
} from "../../../features/admin/appUserMenu/appUserMenuApi";
import {
  useFetchAllDashboardItemsMutation,
  useAddAppUserDashboardMutation,
  useUpdateAppUserDashboardMutation,
} from "../../../features/admin/appUserDashboard/appUserDashboardApi";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";
import { normalizeIconUrl, resolveIconWithFallback } from "../../../utils/appItemIcon";
import {
  addCheckedKeyToItems,
  mergeAppItemsByAction,
  nextCustomItemId,
  normalizeActionKey,
} from "../../../utils/mergeAppItemsByAction";
import {
  Drawer,
  Button,
  IconButton,
  Input,
  Label,
  Badge,
  Spinner,
} from "../../../components/ui";

const FALLBACK_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='M21 15l-5-5L5 21'/%3E%3C/svg%3E";

const emptyForm = { id: "", name: "", action: "", link: "", order_at: "", icon: null };

// One editor for both App Menu and App Dashboard. Tenant + user_type are already
// known (passed in), so there is no "choose client/user" step — just pick items,
// order them, and save.
const AppItemsEditor = ({ kind, tenant, userType, recordId, existingItems, onClose, onSaved }) => {
  const isMenu = kind === "menu";
  const token = () => sessionStorage.getItem("access_token");
  const isUpdateMode = Boolean(recordId);
  const label = isMenu ? "Menu" : "Dashboard";

  // kind-specific hooks
  const [fetchMenuMaster, menuMasterState] = useFetchAllMenuItemsMutation();
  const [fetchDashMaster, dashMasterState] = useFetchAllDashboardItemsMutation();
  const [addMenu] = useAddAppUserMenuMutation();
  const [updateMenu] = useUpdateAppUserMenuMutation();
  const [addDash] = useAddAppUserDashboardMutation();
  const [updateDash] = useUpdateAppUserDashboardMutation();
  const [uploadSingleFile, { isLoading: isUploadingIcon }] = useUploadSingleFileMutation();

  const masterState = isMenu ? menuMasterState : dashMasterState;
  const masterBody = masterState.data?.body;

  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const initializedRef = useRef(false);

  const [modal, setModal] = useState(null); // { mode: 'add' | 'edit', index }
  const [form, setForm] = useState(emptyForm);
  const [iconPreview, setIconPreview] = useState(null);

  // fetch the master catalog once
  useEffect(() => {
    if (isMenu) fetchMenuMaster({ token: token() });
    else fetchDashMaster({ token: token() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMenu]);

  // build the working list: existing tenant items first (checked), master fills gaps
  useEffect(() => {
    if (!masterBody || initializedRef.current) return;
    const built = isUpdateMode
      ? mergeAppItemsByAction(masterBody, existingItems || [])
      : addCheckedKeyToItems(masterBody);
    setItems(built.map((it, idx) => ({ ...it, order_at: it.order_at ?? idx + 1 })));
    initializedRef.current = true;
  }, [masterBody, isUpdateMode, existingItems]);

  const selectedCount = useMemo(() => items.filter((i) => i.checked).length, [items]);

  const toggle = (idx) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, checked: !it.checked } : it)));

  const move = (idx, dir) => {
    setItems((prev) => {
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const removeItem = (idx) => {
    const item = items[idx];
    if (!item) return;
    if (!window.confirm(`Remove "${item.name}" from this ${label.toLowerCase()}?`)) return;
    setItems((prev) =>
      item.tenantOnly
        ? prev.filter((_, i) => i !== idx)
        : prev.map((it, i) => (i === idx ? { ...it, checked: false } : it)),
    );
  };

  // ── add / edit item modal ──────────────────────────────────────────────
  const openAdd = () => {
    setForm({ ...emptyForm, id: String(nextCustomItemId(items)), order_at: String(items.length + 1) });
    setIconPreview(null);
    setModal({ mode: "add" });
  };
  const openEdit = (idx) => {
    const it = items[idx];
    setForm({
      id: String(it.id ?? ""),
      name: it.name || "",
      action: it.action || "",
      link: it.link || "",
      order_at: String(it.order_at || ""),
      icon: null,
    });
    setIconPreview(normalizeIconUrl(it.icon) || null);
    setModal({ mode: "edit", index: idx });
  };
  const closeModal = () => {
    setModal(null);
    setForm(emptyForm);
    setIconPreview(null);
  };

  // Escape closes the item modal first; only then may the drawer close.
  const handleDrawerClose = () => {
    if (modal) return;
    onClose();
  };

  const onFormField = (e) => {
    const { name, value, files } = e.target;
    if (name === "icon") {
      const file = files?.[0] || null;
      setForm((p) => ({ ...p, icon: file }));
      if (file) setIconPreview(URL.createObjectURL(file));
      return;
    }
    setForm((p) => ({ ...p, [name]: value }));
  };

  const uploadIconIfAny = async (fallbackIcon = "") => {
    if (!form.icon) return fallbackIcon;
    const fd = new FormData();
    fd.append("image", form.icon);
    const res = await uploadSingleFile({ token: token(), body: fd }).unwrap();
    return res?.body?.fileLink || fallbackIcon;
  };

  const saveModal = async () => {
    if (!form.name || !form.action || !form.link || !form.order_at) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      if (modal.mode === "add") {
        const actionKey = normalizeActionKey(form.action);
        if (items.some((it) => normalizeActionKey(it.action) === actionKey)) {
          toast.error("An item with this action already exists");
          return;
        }
        const finalIcon = await uploadIconIfAny("");
        const icons = resolveIconWithFallback(finalIcon, finalIcon);
        setItems((prev) => [
          ...prev,
          {
            id: Number(form.id) || nextCustomItemId(prev),
            name: form.name.trim(),
            action: form.action.trim(),
            link: form.link.trim(),
            order_at: Number(form.order_at),
            icon: icons.primaryIcon,
            primaryIcon: icons.primaryIcon,
            fallbackIcon: icons.fallbackIcon,
            checked: true,
            tenantOnly: true,
            companyOverride: true,
          },
        ]);
      } else {
        const idx = modal.index;
        const finalIcon = await uploadIconIfAny(items[idx]?.icon);
        const icons = resolveIconWithFallback(finalIcon, items[idx]?.fallbackIcon || items[idx]?.icon);
        setItems((prev) =>
          prev.map((it, i) =>
            i === idx
              ? {
                  ...it,
                  name: form.name.trim(),
                  action: form.action.trim(),
                  link: form.link.trim(),
                  order_at: Number(form.order_at),
                  icon: icons.primaryIcon,
                  primaryIcon: icons.primaryIcon,
                  fallbackIcon: icons.fallbackIcon,
                  checked: true,
                  companyOverride: true,
                }
              : it,
          ),
        );
      }
      closeModal();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to upload icon");
    }
  };

  // ── save the whole config ──────────────────────────────────────────────
  const buildPayload = () =>
    items
      .filter((it) => it.checked)
      .map((it, idx) => ({
        id: it.id,
        name: it.name,
        action: it.action,
        link: it.link,
        icon: it.icon,
        order_at: idx + 1,
      }));

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload.length) {
      toast.error(`Select at least one ${label.toLowerCase()} item`);
      return;
    }
    const dataKey = isMenu ? "app_menu" : "app_dashboard";
    setSaving(true);
    try {
      if (isUpdateMode) {
        const args = { token: token(), id: recordId, data: { [dataKey]: payload } };
        await (isMenu ? updateMenu(args) : updateDash(args)).unwrap();
        toast.success(`${label} updated`);
      } else {
        const args = {
          token: token(),
          data: {
            tenant_id: tenant.tenant_id,
            tenant_name: tenant.tenant_name,
            user_type: userType,
            [dataKey]: payload,
          },
        };
        await (isMenu ? addMenu(args) : addDash(args)).unwrap();
        toast.success(`${label} created`);
      }
      onSaved?.();
    } catch (err) {
      if (err?.status === 409) toast.error(err?.data?.message || "Already exists");
      else toast.error(err?.data?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const loading = masterState.isLoading;
  let runningPos = 0;

  return (
    <Drawer
      width="lg"
      onClose={handleDrawerClose}
      title={`${isUpdateMode ? "Edit" : "Configure"} App ${label}`}
      chips={[
        { label: tenant.tenant_name, tone: "blue" },
        { label: <span className="capitalize">{userType}</span>, tone: "gray" },
      ]}
      footer={
        <>
          <span className="text-xs text-gray-500">{selectedCount} item(s) will be saved</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
              icon={saving ? <Spinner /> : <FiSave className="h-4 w-4" />}
            >
              {saving ? "Saving…" : `Save ${label}`}
            </Button>
          </div>
        </>
      }
    >
      {/* body */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Choose &amp; order items</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Tick items to include. Use the arrows to set the display order. {selectedCount} selected.
            </p>
          </div>
          <Button variant="soft" icon={<FiPlus className="h-4 w-4" />} onClick={openAdd}>
            Custom item
          </Button>
        </div>

        {loading && <p className="text-sm text-gray-400 py-8 text-center">Loading items…</p>}
        {!loading && items.length === 0 && (
          <p className="text-sm text-gray-400 py-8 text-center">No items found.</p>
        )}

        <div className="space-y-2">
          {items.map((item, idx) => {
            if (item.checked) runningPos += 1;
            return (
              <div
                key={`${item.id}-${idx}`}
                className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${
                  item.checked ? "border-blue-300 bg-blue-50/50" : "border-gray-200 bg-white"
                }`}
              >
                {/* checkbox */}
                <button
                  type="button"
                  onClick={() => toggle(idx)}
                  className={`h-5 w-5 shrink-0 rounded-md border flex items-center justify-center transition-colors ${
                    item.checked
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "border-gray-300 hover:border-blue-400"
                  }`}
                >
                  {item.checked && <HiCheck className="h-3.5 w-3.5" />}
                </button>

                {/* order badge */}
                <span className="w-8 shrink-0 text-center text-xs font-semibold text-gray-400">
                  {item.checked ? `#${runningPos}` : "—"}
                </span>

                {/* icon */}
                <img
                  src={item.primaryIcon || normalizeIconUrl(item.icon) || FALLBACK_ICON}
                  alt={item.name}
                  onError={(e) => {
                    const next = item.fallbackIcon || normalizeIconUrl(item.icon);
                    if (next && e.currentTarget.src !== next) {
                      e.currentTarget.src = next;
                      return;
                    }
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = FALLBACK_ICON;
                  }}
                  className="h-8 w-8 shrink-0 object-contain"
                />

                {/* text */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800 truncate">{item.name}</span>
                    {item.tenantOnly && (
                      <Badge tone="green" className="px-1.5 py-0.5 text-[10px] rounded">
                        custom
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{item.action}</div>
                  <div className="text-xs text-gray-400 truncate">{item.link}</div>
                </div>

                {/* controls */}
                <div className="flex items-center gap-1 shrink-0">
                  <IconButton
                    icon={<FiChevronUp className="h-4 w-4" />}
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="disabled:opacity-30"
                  />
                  <IconButton
                    icon={<FiChevronDown className="h-4 w-4" />}
                    onClick={() => move(idx, 1)}
                    disabled={idx === items.length - 1}
                    className="disabled:opacity-30"
                  />
                  <IconButton tone="blue" icon={<FiEdit2 className="h-4 w-4" />} onClick={() => openEdit(idx)} />
                  {(item.checked || item.tenantOnly) && (
                    <IconButton tone="red" icon={<FiTrash2 className="h-4 w-4" />} onClick={() => removeItem(idx)} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* add / edit item drawer */}
      {modal && (
        <Drawer
          width="md"
          onClose={closeModal}
          title={`${modal.mode === "add" ? "Add" : "Edit"} ${label} Item`}
          footer={
            <>
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveModal} disabled={isUploadingIcon}>
                {isUploadingIcon ? "Saving…" : modal.mode === "add" ? "Add" : "Save"}
              </Button>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-4">
            {modal.mode === "add" && (
              <div>
                <Label>ID *</Label>
                <Input name="id" type="number" min={1} value={form.id} onChange={onFormField} />
              </div>
            )}
            <div className={modal.mode === "add" ? "" : "col-span-2"}>
              <Label>Name *</Label>
              <Input name="name" value={form.name} onChange={onFormField} />
            </div>
            <div>
              <Label>Action *</Label>
              <Input name="action" value={form.action} onChange={onFormField} />
            </div>
            <div>
              <Label>Link *</Label>
              <Input name="link" value={form.link} onChange={onFormField} />
            </div>
            <div>
              <Label>Order *</Label>
              <Input name="order_at" type="number" min={1} value={form.order_at} onChange={onFormField} />
            </div>
            <div>
              <Label>Icon</Label>
              <label className="flex items-center gap-2 px-3 py-2.5 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 text-sm text-gray-500">
                <FiUploadCloud className="h-4 w-4" />
                {form.icon ? "Change" : "Upload"}
                <input type="file" name="icon" accept="image/*" className="hidden" onChange={onFormField} />
              </label>
            </div>
          </div>

          {iconPreview && (
            <div className="mt-3">
              <img src={iconPreview} alt="icon preview" className="h-14 w-14 object-contain" />
            </div>
          )}
        </Drawer>
      )}
    </Drawer>
  );
};

export default AppItemsEditor;
